import React, { useCallback } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import AddressDisplay from './AddressDisplay'
import { useFieldValidation } from './config'
import type {
  CreateBusItemViewModel,
  CreateFerryItemViewModel,
  CreateOtherTransportItemViewModel,
  CreateTrainItemViewModel,
} from '../../Create/models'
import type {
  PatchBusItemViewModel,
  PatchFerryItemViewModel,
  PatchOtherTransportItemViewModel,
  PatchTrainItemViewModel,
} from '../../Edit/models'
import useEnsureGeoPlace from '../Hooks/useEnsureGeoPlace'

import type {
  TripAddressAutocompleteOption,
} from 'tripPlanner/components/AutocompleteInputs/TripAddressAutocomplete'
import TripAddressAutocomplete from 'tripPlanner/components/AutocompleteInputs/TripAddressAutocomplete'
import { useComponentRestrictions } from 'tripPlanner/hooks/Google/useComponentRestrictions'
import type { AutocompletePlaceParam } from 'tripPlanner/types/google'
import {
  ADDRESS_RESPONSE_DETAILS,
} from 'tripPlanner/types/google'
import { convertGoogleAddressComponentsToRegion } from 'tripPlanner/utils'

type CreateOrPatchItemViewModel =
  | CreateBusItemViewModel
  | PatchBusItemViewModel
  | CreateFerryItemViewModel
  | PatchFerryItemViewModel
  | CreateOtherTransportItemViewModel
  | PatchOtherTransportItemViewModel
  | CreateTrainItemViewModel
  | PatchTrainItemViewModel

type Props = {
  autocompleteTypes: AutocompletePlaceParam
  tripId: string
}

const START_GEO_DEP_KEYS: Array<string> = ['startPlace.name']
const END_GEO_DEP_KEYS: Array<string> = ['endPlace.name']

function StartEndAddressFields({ autocompleteTypes, tripId }: Props) {
  const intl = useIntl()
  const { required } = useFieldValidation()
  const methods = useFormContext<CreateOrPatchItemViewModel>()
  const { control, setValue, watch } = methods

  useEnsureGeoPlace(methods, 'startPlace', START_GEO_DEP_KEYS)
  useEnsureGeoPlace(methods, 'endPlace', END_GEO_DEP_KEYS)
  const componentRestrictions = useComponentRestrictions(tripId)

  const startPlace = watch('startPlace')
  const endPlace = watch('endPlace')

  const handleAddressChange = useCallback(
    (
      placeName: 'startPlace' | 'endPlace',
      selectedOption: TripAddressAutocompleteOption,
    ) => {
      setValue(`${placeName}.name`, selectedOption.label)
      setValue(`${placeName}.type`, 'GEO')

      if (selectedOption.details) {
        const { geometry, formatted_address, place_id, address_components } =
          selectedOption.details

        if (address_components) {
          const region =
            convertGoogleAddressComponentsToRegion(address_components)
          if (region) {
            setValue(`${placeName}.region`, region)
          }
        }

        setValue(`${placeName}.id`, place_id)
        setValue(`${placeName}.address`, formatted_address)

        if (geometry?.location) {
          setValue(`${placeName}.point.lat`, geometry.location.lat)
          setValue(`${placeName}.point.lng`, geometry.location.lng)
        }
      }
    },
    [setValue],
  )

  const handleStartAddressChange = useCallback(
    (selectedOption: TripAddressAutocompleteOption) => {
      handleAddressChange('startPlace', selectedOption)
    },
    [handleAddressChange],
  )

  const handleEndAddressChange = useCallback(
    (selectedOption: TripAddressAutocompleteOption) => {
      handleAddressChange('endPlace', selectedOption)
    },
    [handleAddressChange],
  )

  const startLocationLabel = intl.formatMessage({
    id: 'tlxr44arzNPav7gyqfX1',
    defaultMessage: 'Start location',
    description: 'Form label for start location field in transport address fields',
  })

  const endLocationLabel = intl.formatMessage({
    id: 'WXPDEpR1BOxPfYPZZe_r',
    defaultMessage: 'End location',
    description: 'Form label for end location field in transport address fields',
  })

  const searchPlaceholder = intl.formatMessage({
    id: 'A-idEoIJhPNSuyu8fv22',
    defaultMessage: 'Search for location',
    description: 'Placeholder text for location search input in transport address fields',
  })

  const addressLabel = intl.formatMessage({
    id: 'plc7NzURlQkSStNgyibl',
    defaultMessage: 'Address',
    description: 'Label for address display in transport address fields',
  })

  return (
    <>
      <Controller
        name="startPlace.name"
        control={control}
        rules={{ required }}
        render={({ field: { ref, ...rest }, fieldState: { error } }) => (
          <TripAddressAutocomplete
            {...rest}
            ref={ref}
            required
            label={startLocationLabel}
            placeholder={searchPlaceholder}
            autocompleteTypes={autocompleteTypes}
            details={ADDRESS_RESPONSE_DETAILS}
            onPlaceSelect={handleStartAddressChange}
            error={error?.message}
            initPlaceId={startPlace?.type === 'GEO' ? startPlace.id : undefined}
            autocompletionReqParam={{
              componentRestrictions,
            }}
          />
        )}
      />
      {startPlace?.address && (
        <AddressDisplay title={addressLabel} value={startPlace.address} />
      )}

      <Controller
        name="endPlace.name"
        control={control}
        rules={{ required }}
        render={({ field: { ref, ...rest }, fieldState: { error } }) => (
          <TripAddressAutocomplete
            {...rest}
            ref={ref}
            required
            label={endLocationLabel}
            placeholder={searchPlaceholder}
            autocompleteTypes={autocompleteTypes}
            details={ADDRESS_RESPONSE_DETAILS}
            onPlaceSelect={handleEndAddressChange}
            error={error?.message}
            initPlaceId={endPlace?.type === 'GEO' ? endPlace.id : undefined}
            autocompletionReqParam={{
              componentRestrictions,
            }}
          />
        )}
      />
      {endPlace?.address && (
        <AddressDisplay title={addressLabel} value={endPlace.address} />
      )}
    </>
  )
}

export default StartEndAddressFields
